import { useState } from 'react';
import { fetchWithAuth } from '../../../utils/apiAuth';
import config from '../../../config';
import {
  DEFAULT_PREVIOUS_COVER_URL,
  formatSaturdayLabel,
  getNextPreviousReleaseDate
} from '../tabs/magazines/releaseDate';

const apiBaseUrl = config.getApiUrl();

const clone = (value) => JSON.parse(JSON.stringify(value));

export const useMagazines = (showSnackbar) => {
  const [magazines, setMagazines] = useState(null);
  const [magazinesLoading, setMagazinesLoading] = useState(false);
  const [magazinesError, setMagazinesError] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [tempValue, setTempValue] = useState('');
  const [expandedLevel, setExpandedLevel] = useState('beginner');

  const fetchMagazines = async () => {
    try {
      setMagazinesLoading(true);
      setMagazinesError(null);
      const response = await fetchWithAuth(`${apiBaseUrl}/api/magazines`);
      setMagazines(response);
    } catch (err) {
      console.error('Error fetching magazines:', err);
      setMagazinesError(err.message);
    } finally {
      setMagazinesLoading(false);
    }
  };

  /**
   * Persists the whole magazines document and syncs local state on success.
   * Every magazine mutation goes through the same PUT endpoint, so this keeps
   * the request/snackbar/loading handling in a single place.
   */
  const persistMagazines = async (updatedMagazines, messages) => {
    try {
      setMagazinesLoading(true);
      const response = await fetchWithAuth(`${apiBaseUrl}/api/magazines`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedMagazines)
      });

      if (response.success) {
        setMagazines(updatedMagazines);
        showSnackbar(messages.success, 'success');
        return true;
      }

      showSnackbar(messages.failure, messages.failureSeverity || 'error');
      return false;
    } catch (err) {
      console.error(messages.logLabel, err);
      showSnackbar(messages.error, messages.errorSeverity || 'error');
      return false;
    } finally {
      setMagazinesLoading(false);
    }
  };

  const handleEditField = (level, section, field, index = null) => {
    const target = index !== null ? magazines[level][section][index] : magazines[level][section];
    setEditingField({ level, section, field, index });
    setTempValue(target[field] || '');
  };

  const handleSaveField = async () => {
    if (!editingField) return;

    const { level, section, field, index } = editingField;
    const updatedMagazines = clone(magazines);

    if (index !== null) {
      updatedMagazines[level][section][index][field] = tempValue;
    } else {
      updatedMagazines[level][section][field] = tempValue;
    }

    const saved = await persistMagazines(updatedMagazines, {
      success: 'Field saved successfully',
      failure: 'Failed to save field',
      error: 'Error saving field',
      logLabel: 'Error saving field:'
    });

    if (saved) {
      setEditingField(null);
      setTempValue('');
    }
  };

  const handleCancelMagazineEdit = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleClearField = (level, section, field, index = null) => {
    // Only clear the UI text box, do not update database
    const updatedMagazines = clone(magazines);

    if (index !== null) {
      updatedMagazines[level][section][index][field] = '';
    } else {
      updatedMagazines[level][section][field] = '';
    }

    // Update only the UI state
    setMagazines(updatedMagazines);
    showSnackbar('Field cleared on UI (not saved to database)', 'info');
  };

  const handleCoverUploadSuccess = async (level, section, field, index, uploadedUrl) => {
    // Update local state with the uploaded URL
    const updatedMagazines = clone(magazines);

    if (index !== null && index !== undefined) {
      // Updating a previous issue cover
      updatedMagazines[level][section][index][field] = uploadedUrl;
    } else {
      // Updating current issue cover
      updatedMagazines[level][section][field] = uploadedUrl;
    }

    await persistMagazines(updatedMagazines, {
      success: 'Cover uploaded to S3 and saved to database successfully',
      failure: 'Cover uploaded to S3 but failed to save URL to database',
      failureSeverity: 'warning',
      error: 'Cover uploaded to S3 but error saving URL to database',
      errorSeverity: 'warning',
      logLabel: 'Error saving cover URL to database:'
    });
  };

  const handleAddPreviousIssue = async (level) => {
    const updatedMagazines = clone(magazines);

    // Initialize previous array if it doesn't exist
    if (!updatedMagazines[level].previous) {
      updatedMagazines[level].previous = [];
    }

    const previous = updatedMagazines[level].previous;
    const releaseDate = getNextPreviousReleaseDate(previous);
    const title = formatSaturdayLabel(releaseDate);

    // Newest previous slot: Saturday title + release date + shared default cover
    previous.push({
      title,
      link1: '',
      cover1: DEFAULT_PREVIOUS_COVER_URL,
      releaseDate
    });

    await persistMagazines(updatedMagazines, {
      success: `Previous issue for ${title} added successfully`,
      failure: 'Failed to add new issue',
      error: 'Error adding new issue',
      logLabel: 'Error adding new issue:'
    });
  };

  const handleRemovePreviousIssue = async (level, index) => {
    const updatedMagazines = clone(magazines);

    // Remove the issue at the specified index
    updatedMagazines[level].previous.splice(index, 1);

    await persistMagazines(updatedMagazines, {
      success: 'Previous issue removed successfully',
      failure: 'Failed to remove issue',
      error: 'Error removing issue',
      logLabel: 'Error removing issue:'
    });
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar('Copied to clipboard', 'success');
  };

  return {
    magazines,
    magazinesLoading,
    magazinesError,
    editingField,
    tempValue,
    setTempValue,
    expandedLevel,
    setExpandedLevel,
    fetchMagazines,
    handleEditField,
    handleSaveField,
    handleCancelMagazineEdit,
    handleClearField,
    handleCoverUploadSuccess,
    handleAddPreviousIssue,
    handleRemovePreviousIssue,
    handleCopyToClipboard
  };
};
